import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { getAppI18nConfig, initI18n } from 'cfgi18n';
import i18next from 'i18next';
import { AppProps } from 'next/app';
import Head from 'next/head';
import { useEffect, useState } from 'react';
import { I18nextProvider } from 'react-i18next';

import { PUBLIC_CHANNEL } from 'tools/environments';

import { staticProps } from '.';
import { Layout } from '../layout';
import './styles.css';

const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			refetchOnWindowFocus: false,
		},
	},
});

const pageTitle: { [key: string]: string } = {
	mancity: 'Manchester City Football Club',
	palermo: 'Palermo F.C. | Palermo Calcio Official Website',
};

function CustomApp({
	Component,
	pageProps,
}: AppProps<staticProps & { locale: string }>) {
	const [ready, setReady] = useState<boolean>(false);

	useEffect(() => {
		const setup = async () => {
			const { supportedLocales } = await getAppI18nConfig('login');
			await initI18n(pageProps.locale, supportedLocales).then(() => {
				setReady(true);
			});
		};

		setup();
	}, [pageProps.locale]);

	if (!ready) return null;

	return (
		<>
			<Head>
				<title>{pageTitle[PUBLIC_CHANNEL]}</title>
				<meta
					name="viewport"
					content="width=device-width, initial-scale=1.0"
				/>
				<meta
					name="description"
					content="The Official Website of Manchester City F.C. Get the latest Man City news, injury updates, fixtures, player signings, match highlights &amp; much more!"
				/>
				{/* TODO: make favicon dynamic depends on club/theme/channel */}
				<link rel="icon" href="/favicon.ico?v=palermo" />
			</Head>

			<main className="app login-page">
				<I18nextProvider i18n={i18next}>
					<QueryClientProvider client={queryClient}>
						<Layout footerData={pageProps.footerData}>
							<Component {...pageProps} />
						</Layout>
						<ReactQueryDevtools initialIsOpen={false} />
					</QueryClientProvider>
				</I18nextProvider>
			</main>
		</>
	);
}

export default CustomApp;
